import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';

import {
  TwOverlayFallbackHandle,
  TwOverlayFallbackService,
} from '../shared/cdk-fallback';
import { isNativePositioningAvailable } from '../shared/positioning-strategy';

import { TW_POPOVER_CONTEXT } from './tw-popover-context';

/**
 * `tw-popover-content` — the `[popover]` panel body
 * (TWS-122 Wave 2).
 *
 * Host is a `<div>` carrying the native `[popover]` attribute,
 * `role="dialog"`, and the generated popover id so the sibling
 * trigger's `popovertarget` can reach it. The panel moves to the
 * browser top-layer when `showPopover()` runs; CSS anchor
 * positioning pins it to the registered trigger element.
 *
 * ### Role choice
 *
 * `role="dialog"` — popovers can hold form fields, buttons, and
 * other interactive content. Consumers wanting a purely
 * informational hover hint should reach for `[twTooltip]`
 * instead; we intentionally do NOT dual-mode this component.
 *
 * ### Toggle sync
 *
 * Native `toggle` fires on every open/close transition (including
 * light-dismiss + Escape). We mirror `newState` back into the
 * parent's `open` model via the context. A `syncing` guard
 * prevents the `effect()` in this same class from re-triggering
 * `hidePopover()` on an already-closed panel.
 */
@Component({
  selector: 'tw-popover-content',
  standalone: true,
  template: '<ng-content></ng-content>',
  styleUrls: ['./tw-popover.component.scss'],
  host: {
    'class': 'tw-popover__content',
    '[id]': 'ctx.popoverId',
    'popover': 'auto',
    'role': 'dialog',
    '[attr.data-placement]': 'ctx.placement()',
    '(toggle)': 'onToggle($event)',
    // Hover-bridge (TWS-108 Wave 8): forward our own pointer
    // transitions through the context so the trigger directive
    // treats trigger + panel as one hover region. No-op in click
    // mode — the trigger ignores the callback there. Mouseenter
    // also cancels the trigger's pending hide timer so cursor
    // traversal across the small gap between trigger and panel
    // does not kill the popover mid-traversal.
    '(mouseenter)': 'ctx.notifyContentHovered(true)',
    '(mouseleave)': 'ctx.notifyContentHovered(false)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TwPopoverContentComponent implements OnInit {
  protected readonly ctx = inject(TW_POPOVER_CONTEXT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fallback = inject(TwOverlayFallbackService);

  /** Runtime path — memoised. Same pattern as the dropdown menu. */
  private readonly useNative = isNativePositioningAvailable();
  private fallbackHandle: TwOverlayFallbackHandle | null = null;

  /**
   * Re-entrance guard: `onToggle` sets this true while mirroring
   * state back into the context, so the sibling `effect()` does
   * not also fire `hidePopover()` / `showPopover()` on an already-
   * transitioned panel. Same technique as Wave 1's dropdown menu.
   */
  private syncing = false;

  ngOnInit(): void {
    const el = this.host.nativeElement;
    // Wire `position-anchor` inline — the anchor-name property is
    // set on the trigger element by `TwPopoverTriggerComponent`.
    // Writing it here keeps the two slot components decoupled
    // (neither has to know about the other's DOM).
    el.style.setProperty('position-anchor', this.ctx.anchorName);
  }

  constructor() {
    // Mirror parent `open` onto either the native `[popover]`
    // API or the CDK Overlay fallback, depending on the
    // detected positioning strategy. Both branches loop user
    // dismissal back through `ctx.setOpen(false)` — native via
    // the `toggle` event, fallback via the service's
    // `onDismiss` callback.
    effect(() => {
      if (this.syncing) return;
      const shouldBeOpen = this.ctx.open();
      if (this.useNative) {
        this.syncNative(shouldBeOpen);
      } else {
        this.syncFallback(shouldBeOpen);
      }
    });

    this.destroyRef.onDestroy(() => {
      if (this.useNative) {
        const el = this.host.nativeElement as HTMLElement & {
          hidePopover?: () => void;
          matches(sel: string): boolean;
        };
        if (typeof el.hidePopover === 'function' && el.matches(':popover-open')) {
          try {
            el.hidePopover();
          } catch {
            /* noop */
          }
        }
      } else if (this.fallbackHandle) {
        this.fallbackHandle.dispose();
        this.fallbackHandle = null;
      }
    });
  }

  private syncNative(shouldBeOpen: boolean): void {
    const el = this.host.nativeElement;
    const hasShow = typeof (el as HTMLElement & {
      showPopover?: () => void;
    }).showPopover === 'function';
    if (!hasShow) return;

    const typed = el as HTMLElement & {
      showPopover: () => void;
      hidePopover: () => void;
      matches(sel: string): boolean;
    };

    const isOpen = typed.matches(':popover-open');
    try {
      if (shouldBeOpen && !isOpen) typed.showPopover();
      else if (!shouldBeOpen && isOpen) typed.hidePopover();
    } catch {
      // Swallow — usually a race between rapid open/close signal
      // flips before the DOM has reflected the previous state.
      // The signal is the source of truth; the next effect run
      // reconciles.
    }
  }

  private syncFallback(shouldBeOpen: boolean): void {
    const triggerEl = this.ctx.triggerElement();
    if (shouldBeOpen && !this.fallbackHandle && triggerEl) {
      this.fallbackHandle = this.fallback.openAt(
        triggerEl,
        this.host.nativeElement,
        this.ctx.placement(),
        {
          lightDismiss: true,
          onDismiss: () => this.ctx.setOpen(false),
        },
      );
    } else if (!shouldBeOpen && this.fallbackHandle) {
      this.fallbackHandle.dispose();
      this.fallbackHandle = null;
    }
  }

  /**
   * Native `toggle` fires on every state transition — mirror
   * `newState` back into the context so `[(open)]` consumers stay
   * in lockstep with light-dismiss / Escape.
   *
   * `ToggleEvent` is in the DOM lib but not every Angular project
   * ships that type; narrow-cast to the fields we need.
   */
  protected onToggle(event: Event): void {
    const typed = event as Event & { newState?: 'open' | 'closed' };
    const isOpen = typed.newState === 'open';
    this.syncing = true;
    try {
      this.ctx.setOpen(isOpen);
    } finally {
      this.syncing = false;
    }
  }
}
